/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useCallback, useEffect } from "react";

import { usePlayerController, usePlayerSettings } from "../BasePlayer";

import { usePlayerFullScreen } from "./usePlayerFullScreen";
import { usePlayerPlayingState } from "./usePlayerPlayingState";
import { usePlayerVolume } from "./usePlayerVolume";

type Props = {
  enabled: boolean;
};

const isEventFromInteractiveElement = ({ target }: KeyboardEvent): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    (target instanceof HTMLElement && target.isContentEditable) ||
      target.closest(
        "a[href], button, input, select, textarea, [role='button']",
      ),
  );
};

export const useKeyboardControls = ({ enabled = true }: Props) => {
  const { toggle: toggleFullScreen } = usePlayerFullScreen();
  const { togglePlay } = usePlayerPlayingState();
  const { toggleMute, changeVolume } = usePlayerVolume();

  const { player } = usePlayerController();

  const {
    ui: { seekJumpSeconds },
  } = usePlayerSettings();

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const { code } = event;

      switch (code) {
        case "ArrowRight":
          player.seekForward(seekJumpSeconds);
          break;
        case "ArrowLeft":
          player.seekBackward(seekJumpSeconds);
          break;
        case "ArrowUp":
          changeVolume(0, 0.1);
          break;
        case "ArrowDown":
          changeVolume(0, -0.1);
          break;
        case "Space":
          if (isEventFromInteractiveElement(event)) {
            return;
          }

          event.preventDefault();
          togglePlay();
          break;
        case "KeyF":
          toggleFullScreen();
          break;
        case "KeyM":
          toggleMute();
          break;
      }
    },
    [changeVolume, togglePlay, toggleFullScreen, toggleMute, player],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};
